/**
 * Plain-text script editor shared by the admin live-assist view and the library
 * editor. Loads the script once (the editor then owns edits), debounces, and
 * preserves block ids via reconcileIds so a live presenter's reading position
 * survives the edit. Emits a parsed Project on change.
 */
import { useRef, useState } from 'react';
import { parseScript, reconcileIds, serializeScript } from '@goanna-im/shared';
import type { Project } from '@goanna-im/shared';

export function ScriptTextEditor({
  project,
  onChange,
  minHeight = 300,
}: {
  project: Project;
  onChange: (p: Project) => void;
  minHeight?: number;
}) {
  const [text, setText] = useState(() => serializeScript(project));
  const [title, setTitle] = useState(project.title);
  const prevRef = useRef<Project>(project);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const push = (nextText: string, nextTitle: string) => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      const parsed = parseScript(nextText, nextTitle || 'Untitled script');
      const reconciled = reconcileIds(prevRef.current, parsed);
      reconciled.version = (prevRef.current.version ?? 0) + 1;
      reconciled.modifiedAt = Date.now();
      prevRef.current = reconciled;
      onChange(reconciled);
    }, 160);
  };

  return (
    <div className="stack">
      <input
        aria-label="Script title"
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
          push(text, e.target.value);
        }}
      />
      <textarea
        className="script-editor"
        aria-label="Script text"
        value={text}
        spellCheck
        style={{ minHeight }}
        onChange={(e) => {
          setText(e.target.value);
          push(e.target.value, title);
        }}
      />
      <p className="muted" style={{ margin: 0, fontSize: '0.8rem' }}>
        <code># Heading</code>, <code>[PAUSE]</code>, <code>[CAMERA 2]</code>. Lines ending in ? become
        questions.
      </p>
    </div>
  );
}
